import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useErrand } from '../contexts/ErrandContext';
import { supabase } from '../lib/supabase';
import { DashboardStatsSkeleton } from '../components/UI/LoadingSkeleton';
import EmptyState from '../components/UI/EmptyState';

interface AnalyticsData {
  totalErrands: number;
  completedErrands: number;
  cancelledErrands: number;
  totalSpent: number; // For customers: total spent; for drivers/couriers: total earned
  totalEarned: number; // For drivers/couriers: total earnings
  averageErrandCost: number;
  mostUsedErrandType: string;
  errandsByStatus: Record<string, number>;
  errandsByMonth: Array<{ month: string; count: number; amount: number; earned?: number }>;
  averageCompletionTime: number; // in hours
}

const AnalyticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const { userErrands, fetchUserErrands, driverErrands, fetchDriverErrands } = useErrand();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year' | 'all'>('month');

  useEffect(() => {
    if (user) {
      fetchUserErrands();
      // Also fetch driver errands if user is a driver/courier
      if (user.role === 'driver' || user.isDriver || user.role === 'courier') {
        fetchDriverErrands();
      }
    }
  }, [user, fetchUserErrands, fetchDriverErrands]);

  useEffect(() => {
    // Calculate analytics even if arrays are empty (to show empty state properly)
    calculateAnalytics();
  }, [userErrands, driverErrands, timeRange]);

  const calculateAnalytics = () => {
    setLoading(true);

    const now = new Date();
    let startDate: Date;

    switch (timeRange) {
      case 'week':
        // Past 7 days from now
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        // Start of current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'year':
        // Start of current year
        startDate = new Date(now.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        startDate = new Date(0);
    }

    // Combine user errands (as customer) and driver errands (as driver/courier)
    const allErrands = [...userErrands];

    // Add driver errands if user is a driver/courier (avoid duplicates)
    if (user && (user.role === 'driver' || user.isDriver || user.role === 'courier')) {
      driverErrands.forEach(driverErrand => {
        if (!allErrands.find(e => e.id === driverErrand.id)) {
          allErrands.push(driverErrand);
        }
      });
    }

    // Filter errands by time range and payment status
    // Only include errands with completed payments for financial calculations
    const filteredErrands = allErrands.filter(
      errand => new Date(errand.created_at) >= startDate
    );

    const totalErrands = filteredErrands.length;
    const completedErrands = filteredErrands.filter(e => e.status === 'completed').length;
    const cancelledErrands = filteredErrands.filter(e => e.status === 'cancelled').length;

    // Determine if user is a driver/courier or customer
    const isDriverOrCourier = user && (user.role === 'driver' || user.isDriver || user.role === 'courier');

    // For customers: total spent (only from errands they created where payment is completed)
    // For drivers/couriers: total earned (only from errands they completed where payment is completed)
    const customerErrands = filteredErrands.filter(e =>
      e.user_id === user?.id && e.payment_status === 'completed'
    );
    const driverCompletedErrands = filteredErrands.filter(e =>
      e.driver_id === user?.id && e.payment_status === 'completed' && e.status === 'completed'
    );

    const totalSpent = customerErrands.reduce((sum, e) => sum + (e.total_cost || 0), 0);
    const totalEarned = driverCompletedErrands.reduce((sum, e) => sum + (e.driver_earnings || 0), 0);

    const averageErrandCost = completedErrands > 0
      ? (isDriverOrCourier ? totalEarned : totalSpent) / completedErrands
      : 0;

    // Most used errand type
    const errandTypeCounts: Record<string, number> = {};
    filteredErrands.forEach(e => {
      const typeName = e.errand_types?.name || 'Unknown';
      errandTypeCounts[typeName] = (errandTypeCounts[typeName] || 0) + 1;
    });
    const mostUsedErrandType = Object.entries(errandTypeCounts)
      .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';

    // Errands by status
    const errandsByStatus: Record<string, number> = {};
    filteredErrands.forEach(e => {
      errandsByStatus[e.status] = (errandsByStatus[e.status] || 0) + 1;
    });

    // Errands by month - separate spent vs earned
    const errandsByMonth: Record<string, { count: number; amount: number; earned?: number }> = {};
    filteredErrands.forEach(e => {
      const month = new Date(e.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      if (!errandsByMonth[month]) {
        errandsByMonth[month] = { count: 0, amount: 0, earned: 0 };
      }
      errandsByMonth[month].count++;
      // Only count if payment is completed
      if (e.payment_status === 'completed') {
          // Amount is spent (for customer errands) or earned (for driver errands)
        if (e.user_id === user?.id) {
          errandsByMonth[month].amount += e.total_cost || 0;
        }
        if (e.driver_id === user?.id && e.status === 'completed') {
          errandsByMonth[month].earned = (errandsByMonth[month].earned || 0) + (e.driver_earnings || 0);
        }
      }
    });

    const errandsByMonthArray = Object.entries(errandsByMonth)
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());

    // Average completion time
    const completedWithTimes = filteredErrands.filter(
      e => e.status === 'completed' && e.completed_at && e.created_at
    );
    const totalCompletionTime = completedWithTimes.reduce((sum, e) => {
      const created = new Date(e.created_at).getTime();
      const completed = new Date(e.completed_at!).getTime();
      return sum + (completed - created);
    }, 0);
    const averageCompletionTime = completedWithTimes.length > 0
      ? totalCompletionTime / completedWithTimes.length / (1000 * 60 * 60) // Convert to hours
      : 0;

    setAnalytics({
      totalErrands,
      completedErrands,
      cancelledErrands,
      totalSpent,
      totalEarned,
      averageErrandCost,
      mostUsedErrandType,
      errandsByStatus,
      errandsByMonth: errandsByMonthArray,
      averageCompletionTime
    });
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <DashboardStatsSkeleton />
        </div>
      </div>
    );
  }

  if (!analytics || analytics.totalErrands === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <EmptyState
            icon="📊"
            title="No Analytics Data"
            message="Start creating errands to see your analytics and insights."
            actionLabel="Request Errand"
            actionPath="/errand/request"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600">Insights into your errand activity</p>
        </div>

        {/* Time Range Selector */}
        <div className="mb-4 sm:mb-6 overflow-x-auto">
          <div className="flex space-x-2 min-w-max pb-2">
            {(['week', 'month', 'year', 'all'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-colors whitespace-nowrap ${
                  timeRange === range
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Errands</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1 truncate">{analytics.totalErrands}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                <span className="text-lg sm:text-2xl">📦</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Completed</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600 mt-1 truncate">{analytics.completedErrands}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                <span className="text-lg sm:text-2xl">✅</span>
              </div>
            </div>
          </div>

          {user && (user.role === 'driver' || user.isDriver || user.role === 'courier') ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Earned</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600 mt-1 truncate">
                    P{analytics.totalEarned.toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                  <span className="text-lg sm:text-2xl">💰</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Spent</p>
                  <p className="text-xl sm:text-2xl font-bold text-purple-600 mt-1 truncate">
                    P{analytics.totalSpent.toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                  <span className="text-lg sm:text-2xl">💰</span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Avg. Cost</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-600 mt-1 truncate">
                  P{analytics.averageErrandCost.toFixed(2)}
                </p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
                <span className="text-lg sm:text-2xl">📊</span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          {/* Status Distribution */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 overflow-x-auto">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Errands by Status</h3>
            <div className="space-y-2 sm:space-y-3 min-w-0">
              {Object.entries(analytics.errandsByStatus).map(([status, count]) => (
                <div key={status} className="min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs sm:text-sm font-medium text-gray-700 capitalize truncate">
                      {status.replace('_', ' ')}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-600 flex-shrink-0 ml-2">{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{
                        width: `${(count / analytics.totalErrands) * 100}%`
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Trends */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 overflow-x-auto">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Monthly Trends</h3>
            <div className="space-y-2 sm:space-y-3 min-w-0">
              {analytics.errandsByMonth.slice(-6).map(({ month, count, amount, earned }) => (
                <div key={month} className="flex items-center justify-between min-w-0">
                  <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">{month}</span>
                  <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0 ml-2">
                    <span className="text-xs sm:text-sm text-gray-600">{count} errands</span>
                    {user && (user.role === 'driver' || user.isDriver || user.role === 'courier') ? (
                      <span className="text-xs sm:text-sm font-semibold text-green-600">
                        P{(earned || 0).toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-xs sm:text-sm font-semibold text-green-600">
                        P{amount.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Additional Insights */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 overflow-x-auto">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Additional Insights</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Most Used Type</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900 mt-1 truncate">{analytics.mostUsedErrandType}</p>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Avg. Completion Time</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900 mt-1">
                {analytics.averageCompletionTime.toFixed(1)} hours
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Cancellation Rate</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900 mt-1">
                {((analytics.cancelledErrands / analytics.totalErrands) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;

